import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { DashboardPageFormBody, DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import {
  AutocompleteSingleSelect,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@shigo-ui/ui';

import { demandPartnerBidderCodes } from '../../bidderCodes';
import {
  resolveDemandPartnerBidderConfig,
  fallbackDemandPartnerBidderConfig,
} from '../../bidderConfig';
import type { DemandPartnerFormValue } from '../../formSchema';
import { DemandPartnerDynamicField, DemandPartnerStatus } from '../../model';
import { useDemandPartnerBidderConfigQuery, useDemandPartnerOwnersQuery } from '../../hooks';
import { FormSwitchField } from './FormSwitchField';

const bidderCodeOptions = demandPartnerBidderCodes.map((code) => ({ id: code, label: code }));

export function DemandPartnerGeneralFields() {
  const { register, control, formState, setValue } = useFormContext<DemandPartnerFormValue>();
  const ownersQuery = useDemandPartnerOwnersQuery();
  const configQuery = useDemandPartnerBidderConfigQuery();
  const bidderCode = useWatch({ control, name: 'bidderCode' });
  const ownerId = useWatch({ control, name: 'ownerId' });
  const owners = ownersQuery.data ?? [];
  const dynamic = useMemo(
    () =>
      resolveDemandPartnerBidderConfig(
        bidderCode,
        configQuery.data ?? fallbackDemandPartnerBidderConfig,
      ),
    [bidderCode, configQuery.data],
  );
  return (
    <DashboardPageFormBody width="lg" title="General">
      <div className="grid gap-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <DashboardPageFormField
            label="Name"
            required
            error={formState.errors.displayName?.message}
          >
            <Controller
              control={control}
              name="displayName"
              render={({ field }) => <Input {...field} />}
            />
          </DashboardPageFormField>
        </div>
        <DashboardPageFormField
          label="Owner"
          hint="The Owner is the company that owns this demand partner account. It provides the supply identity and SCHAIN."
        >
          <Select
            value={ownerId || 'none'}
            onValueChange={(value) => {
              setValue('ownerId', value === 'none' ? '' : value, { shouldDirty: true });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Choose owner" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No owner</SelectItem>
              {owners.map((owner) => (
                <SelectItem key={owner.id} value={owner.id}>
                  {owner.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </DashboardPageFormField>
        <DashboardPageFormField
          label="TCF Vendor ID"
          hint="Demand-partner-level TCF vendor ID."
          error={formState.errors.tcfVendorId?.message}
        >
          <Input {...register('tcfVendorId')} />
        </DashboardPageFormField>
        <DashboardPageFormField
          label="Bidder code"
          required
          hint="List of all available bidders"
          error={formState.errors.bidderCode?.message}
        >
          <AutocompleteSingleSelect
            label="Bidder code"
            value={bidderCode}
            options={bidderCodeOptions}
            onValueChange={(value) => {
              setValue('bidderCode', value, { shouldDirty: true });
            }}
            placeholder="Choose bidder code"
            searchPlaceholder="Search bidder codes"
            emptyMessage="No bidder codes found."
          />
        </DashboardPageFormField>
        <DashboardPageFormField label="Status" required>
          <Select
            value={useWatch({ control, name: 'status' })}
            onValueChange={(value) => {
              setValue('status', value as DemandPartnerStatus);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(DemandPartnerStatus).map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </DashboardPageFormField>
      </div>
      <DashboardPageFormField label="Address">
        <Textarea {...register('address')} rows={3} />
      </DashboardPageFormField>
      <div className="grid gap-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <FormSwitchField name="approvalsRequired" label="Approvals required" />
        </div>
        {dynamic.visible.includes(DemandPartnerDynamicField.UrlCleanup) ? (
          <FormSwitchField name="urlCleanup" label="URL cleanup" />
        ) : null}
        {dynamic.visible.includes(DemandPartnerDynamicField.SkipRequestsWithoutContentUrl) ? (
          <FormSwitchField
            name="skipRequestsWithoutContentUrl"
            label="Skip requests without content URL"
          />
        ) : null}
        {dynamic.visible.includes(DemandPartnerDynamicField.NullifyContentChannelNetwork) ? (
          <FormSwitchField
            name="nullifyContentChannelNetwork"
            label="Nullify content channel/network"
          />
        ) : null}
      </div>
    </DashboardPageFormBody>
  );
}
